import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Boxes, Plus, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import nurseryService from '../../services/nursery';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import LotPlaceFilter from '../../components/nursery/LotPlaceFilter';
import LotStageDialog, { type LotChangeMode } from '../../components/nursery/LotStageDialog';
import { useStockSeat } from '../../hooks/useStockSeat';
import { cn } from '../../utils/cn';
import { compareSlots, slotWhere } from '../../utils/nurseryRowSlots';
import { LOT_STAGE_LABELS } from '../../types/nursery';
import type { NurseryLot, LotStage, LotStatus, PlaceFilter } from '../../types/nursery';

const STAGES: LotStage[] = ['sown', 'seedling', 'transplanted', 'grafted', 'budded', 'growing', 'potted', 'ready', 'dispatched'];

function LotList() {
  const { t } = useTranslation('nursery', { keyPrefix: 'LotList' });
  const orgTz = useOrgTimezone();
  const navigate = useNavigate();
  const [lots, setLots] = useState<NurseryLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>('');
  const [status, setStatus] = useState<LotStatus | ''>('');
  const [place, setPlace] = useState<PlaceFilter>({});
  const [reloads, setReloads] = useState(0);
  // Ticked lots are changed together: ready to sell, another stage, out of stock.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [change, setChange] = useState<{ mode: LotChangeMode; stage?: LotStage } | null>(null);
  const { hasAccess: canOperate } = useStockSeat('operate');
  const { hasAccess: canManage } = useStockSeat('manage');

  useEffect(() => {
    setLoading(true);
    setError(null);
    nurseryService.getLots({ ...(stage ? { stage } : {}), ...(status ? { status } : {}), ...place })
      .then(res => { if (res.success) setLots(res.data || []); else setError(t('errors.loadLotsFailed')); })
      .catch(e => setError(e instanceof Error ? e.message : t('errors.loadFailed')))
      .finally(() => setLoading(false));
  }, [stage, status, place, reloads, t]);

  // A filter that hides a ticked lot unticks it, so nothing unseen is changed.
  useEffect(() => {
    setSelected(prev => {
      const ids = new Set(lots.map(l => l.id));
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [lots]);

  const filtered = Boolean(stage || status || place.farm_id);
  const selectable = (l: NurseryLot) => l.status === 'active' || l.status === 'archived';
  const toggle = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const chosen = lots.filter(l => selected.has(l.id));
  const chosenActive = chosen.filter(l => l.status === 'active');
  const chosenArchived = chosen.filter(l => l.status === 'archived');
  const chosenTrees = chosen.reduce((n, l) => n + l.current_count, 0);
  const dialogLots = change?.mode === 'restore' ? chosenArchived : chosenActive;
  // Read down the page as the rows are walked: row, then position along it.
  const sorted = useMemo(
    () => [...lots].sort((a, b) => compareSlots(a, b) || (a.cultivar_name ?? '').localeCompare(b.cultivar_name ?? '')),
    [lots],
  );

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Boxes className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" size="sm" onClick={() => navigate('/nursery/lots/new')}>
            <Plus className="h-4 w-4" /> {t('placeLot')}
          </Button>
        }
      />
      <div className={cn('max-w-7xl mx-auto px-4 py-6', selected.size > 0 && 'pb-40')}>
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <select value={stage} onChange={e => setStage(e.target.value)} aria-label={t('stage')}
            className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            <option value="">{t('allStages')}</option>
            {STAGES.map(s => <option key={s} value={s}>{t(`stages.${s}`)}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value as LotStatus | '')} aria-label={t('status')}
            className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            <option value="">{t('allStatuses')}</option>
            <option value="active">{t('statusFilter.active')}</option>
            <option value="archived">{t('statusFilter.archived')}</option>
            <option value="depleted">{t('statusFilter.depleted')}</option>
            <option value="sold">{t('statusFilter.sold')}</option>
            <option value="culled">{t('statusFilter.culled')}</option>
          </select>
          <LotPlaceFilter value={place} onChange={setPlace} />
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>}

        {loading ? (
          <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-12 bg-neutral-100 rounded-lg animate-pulse" />)}</div>
        ) : lots.length === 0 && filtered ? (
          <p className="text-center py-16 text-neutral-500">{t('noMatch')}</p>
        ) : lots.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Boxes className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{t('noLots')}</p>
            <Button variant="primary" className="mt-4" onClick={() => navigate('/nursery/lots/new')}>
              <Plus className="h-4 w-4" /> {t('placeFirst')}
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 text-left text-xs">
                <tr>
                  {canOperate && (
                    <th className="pl-4 py-2 w-8">
                      <input type="checkbox" aria-label={t('selectAll')}
                        checked={lots.some(selectable) && lots.filter(selectable).every(l => selected.has(l.id))}
                        onChange={e => setSelected(e.target.checked ? new Set(lots.filter(selectable).map(l => l.id)) : new Set())} />
                    </th>
                  )}
                  <th className="px-4 py-2 font-medium">{t('columns.cultivar')}</th>
                  <th className="px-4 py-2 font-medium">{t('columns.type')}</th>
                  <th className="px-4 py-2 font-medium">{t('columns.stage')}</th>
                  <th className="px-4 py-2 font-medium">{t('columns.location')}</th>
                  <th className="px-4 py-2 font-medium text-right">{t('columns.count')}</th>
                  <th className="px-4 py-2 font-medium">{t('columns.readyDate')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sorted.map(lot => (
                  <tr key={lot.id} className={cn('hover:bg-neutral-50', selected.has(lot.id) && 'bg-primary-50')}>
                    {canOperate && (
                      <td className="pl-4 py-2">
                        {selectable(lot) && (
                          <input type="checkbox" aria-label={t('selectLot', { name: `${lot.cultivar_name}${lot.lot_code ? ` ${lot.lot_code}` : ''}` })}
                            checked={selected.has(lot.id)} onChange={() => toggle(lot.id)} />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <Link to={`/nursery/lots/${lot.id}`} className="text-primary-600 hover:underline">
                        {lot.cultivar_name}{lot.rootstock_name ? ` / ${lot.rootstock_name}` : ''}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-neutral-600">{lot.tree_type_code}</td>
                    <td className="px-4 py-2 text-neutral-600">
                      <span className={lot.current_stage === 'ready' ? 'text-green-700 font-medium' : undefined}>
                        {t(`stages.${lot.current_stage}`, { defaultValue: LOT_STAGE_LABELS[lot.current_stage as LotStage] ?? lot.current_stage })}
                      </span>
                      {lot.status !== 'active' && (
                        <span className="ml-2 text-xs rounded bg-neutral-100 px-1.5 py-0.5 text-neutral-500">
                          {lot.status === 'archived' ? t('outOfStock') : t(`statuses.${lot.status}`, { defaultValue: lot.status })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-neutral-600">{lot.location_name ? slotWhere(lot) : '—'}</td>
                    <td className="px-4 py-2 text-right font-medium text-neutral-800">{lot.current_count.toLocaleString()}</td>
                    {/* The expected date. A lot already at the ready stage is ready now,
                        whether or not anyone recorded a date for it. */}
                    <td className="px-4 py-2 text-neutral-600">
                      {lot.ready_date ? formatLocalDateInTz(lot.ready_date, orgTz)
                        : lot.current_stage === 'ready' ? t('now') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-neutral-800 w-full sm:w-auto sm:mr-2">
              {t('selectedSummary', { n: selected.size, trees: chosenTrees.toLocaleString() })}
            </span>
            {chosenActive.length > 0 && (
              <>
                <Button variant="success" size="sm" onClick={() => setChange({ mode: 'stage', stage: 'ready' })}>
                  <CheckCircle2 className="h-4 w-4" /> {t('readyToSell')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setChange({ mode: 'stage' })}>{t('changeStage')}</Button>
                {canManage && (
                  <Button variant="outline" size="sm" onClick={() => setChange({ mode: 'archive' })}>{t('takeOutOfStock')}</Button>
                )}
              </>
            )}
            {canManage && chosenArchived.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setChange({ mode: 'restore' })}>
                {t('putBackInStock', { n: chosenArchived.length })}
              </Button>
            )}
            <button type="button" className="text-sm text-neutral-500 underline ml-auto" onClick={() => setSelected(new Set())}>{t('clear')}</button>
          </div>
        </div>
      )}

      {change && dialogLots.length > 0 && (
        <LotStageDialog
          lots={dialogLots}
          mode={change.mode}
          initialStage={change.stage}
          onClose={() => setChange(null)}
          onDone={(result) => {
            setSelected(prev => new Set([...prev].filter(id => !result.changed.includes(id))));
            setReloads(n => n + 1);
          }}
        />
      )}
    </>
  );
}

export default LotList;
